<template>
  <main :aria-busy="isLoading ? 'true' : 'false'"
        class="education-details-page"
  >
    <div class="container education-details-container">
      <div class="education-details-layout">
        <div v-if="isLoading" class="education-details-loading" role="status">
          <DefaultLoading minHeight="60vh"/>
        </div>

        <template v-else>
          <EducationDetailsHero :item="item"
                                :success-message="successMessageSend"
                                class="education-details-layout__hero"
          />

          <EducationDetailsSidebar :auth-user-id="authUser ? authUser.id : null"
                                   :item="item"
                                   :message="form.content"
                                   :request-number="request_number"
                                   class="education-details-layout__sidebar"
                                   @purchase="purchase"
                                   @send-message="sendMessage"
                                   @show-message="showMessageSendModal"
                                   @show-requests="showModalRequestListModal"
                                   @show-user="showUserModal"
                                   @update:message="form.content = $event"
          />

          <EducationDetailsTabs :external-package-url="anzeraPackageItemUrl" :item="item"
                                :message="form.content"
                                :request-types="request_types"
                                :scorm-package-url="scormPackageItemUrl"
                                :selected-content="selected_content"
                                :success-message="successMessage"
                                class="education-details-layout__tabs"
                                @content-select="showContentModal"
                                @remove-purchase="removePurchase"
                                @send-message="saveForm"
                                @update:message="form.content = $event"
          />
        </template>
      </div>
    </div>

    <UserProfile
        v-if="formattedUserDetail && activeItem"
        :item="activeItem"
        :user_detail="formattedUserDetail"
    />
  </main>
</template>

<script>
import axios from "axios";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import EducationDetailsHero from "./components/EducationDetailsHero.vue";
import EducationDetailsSidebar from "./components/EducationDetailsSidebar.vue";
import EducationDetailsTabs from "./components/EducationDetailsTabs.vue";
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import UserProfile from "@/view/company/User/UserProfile/index.vue";
import {
  CREATE_ITEM,
  DELETE_ITEM_BY_ID,
  ERROR,
  GET_ITEM_DETAIL_BY_ID,
} from "@/core/services/store/REST.module";
import purchaseModule, {
  GET_REQUEST_COUNT as GET_PURCHASE_USER_REQUEST_COUNT,
  MODULE_NAME as PURCHASE_MODULE_NAME,
} from "@/core/services/store/training_purchase.module";
import userDetailModule, {
  GET_ITEM as GET_USER_DETAIL,
  ITEM as USER_DETAIL,
  MODULE_NAME as USER_DETAIL_MODULE,
} from "@/core/services/store/user_detail.module";
import { getSubCompanyId } from "@/core/services/store.js";
import store from "@/core/services/index";

const UIkit = window.UIkit;

export default {
  name: "RequestEducationDetails",
  components: {
    EducationDetailsHero,
    EducationDetailsSidebar,
    EducationDetailsTabs,
    DefaultLoading,
    UserProfile,
  },
  data() {
    return {
      activeItem: null,
      selectedUser: null,
      restUrl: "api/training/",
      trainingPurchaseUrl: "api/training/purchase/",
      purchaseUrl: "api/training-purchase/",
      scormUrl: "api/training-content/get-scorm/",
      scormPackageItemUrl: null,
      anzeraPackageItemUrl: null,
      successMessage: null,
      successMessageSend: null,
      errorMessage: null,
      isLoading: true,
      detailRequestId: 0,
      item: {},
      form: {
        content: "",
      },
      selected_content: null,
      request_number: 0,
      request_types: {
        1: this.$t("general.all_users"),
      },
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + this.secureLocalStorage("token"),
        Company: getSubCompanyId?.value,
      },
    };
  },
  beforeCreate() {
    const registerStoreModule = (moduleName, storeModule) => {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    };

    registerStoreModule(PURCHASE_MODULE_NAME, purchaseModule);
    registerStoreModule(USER_DETAIL_MODULE, userDetailModule);
  },
  computed: {
    formattedUserDetail() {
      if (!this.user_detail || isEmpty(this.user_detail)) return null;
      return this.user_detail;
    },
    user_detail() {
      return store.getters[USER_DETAIL_MODULE + "/" + USER_DETAIL];
    },
    error() {
      return this.$store.getters[ERROR];
    },
  },
  watch: {
    formattedUserDetail(value) {
      if (!value) return;

      const modal = UIkit.modal("#modal-profile");
      if (modal) modal.show();
    },
    successMessage() {
      this.debouncedResetValues();
    },
    errorMessage() {
      this.debouncedResetValues();
    },
    successMessageSend() {
      this.debouncedResetValues();
    },
  },
  created() {
    this.debouncedResetValues = debounce(this.resetMessages, 3000);
  },
  mounted() {
    this.loadTrainingFromRoute(this.$route.params.id);
  },
  beforeRouteUpdate(to, from, next) {
    if (to.params.id !== from.params.id) {
      this.loadTrainingFromRoute(to.params.id);
    }
    next();
  },
  beforeDestroy() {
    [
      "#admin_content_modal",
      "#message_modal",
      "#message_modal_user",
      "#modal-request-list",
    ].forEach(this.destroyModal);

    if (this.debouncedResetValues && this.debouncedResetValues.cancel) {
      this.debouncedResetValues.cancel();
    }
  },
  methods: {
    destroyModal(selector) {
      const element = document.querySelector(selector);
      if (!element) return;

      const modal = UIkit.modal(element);
      if (modal) modal.$destroy(true);
    },
    downloadURI(uri) {
      window.open(uri);
      this.successMessage = this.$t("general.content_download_successfully");
    },
    showContentModal(selectedContent) {
      this.selected_content = selectedContent;

      if ([4, 5].includes(selectedContent.training_file_type_id)) {
        this.downloadURI(selectedContent.file.file_link);
        return;
      }

      if (selectedContent.training_file_type_id === 1) {
        this.getScormEducationUrl(selectedContent.id);
      }

      if (selectedContent.training_file_type_id === 10) {
        this.anzeraPackageItemUrl =
            selectedContent.link || selectedContent.training_content || null;
      }

      const modal = UIkit.modal("#admin_content_modal");
      if (modal) modal.show();
    },
    showUserModal(user) {
      this.activeItem = user;
      this.getUserDetail(user.id);
    },
    showModalRequestListModal() {
      const modal = UIkit.modal("#modal-request-list");
      if (modal) modal.show();
    },
    showMessageSendModal(id) {
      this.form.content = "";
      this.selectedUser = id;

      const modal = UIkit.modal("#message_modal_user");
      if (modal) modal.show();
    },
    getUserDetail(id) {
      store.dispatch(USER_DETAIL_MODULE + "/" + GET_USER_DETAIL, {
        url: "api/user/detail-admin/" + id,
      });
    },
    async saveForm() {
      const formData = new FormData();
      const groupFormData = new FormData();
      const userId = JSON.parse(this.secureLocalStorage("user")).id;

      formData.append("message", this.form.content);
      formData.append("sender_user_id", userId);
      groupFormData.append("users[]", [-1]);

      try {
        const groupResponse = await axios.post(
            "api/store-group",
            groupFormData,
            {
              headers: this.headers,
            }
        );
        const groupId = groupResponse.data.id ?? groupResponse.data;
        formData.append("message_group_id", groupId);

        await axios.post("api/send-message", formData, {
          headers: this.headers,
        });

        UIkit.modal("#message_modal").hide();
        this.successMessageSend = this.$t("general.message_sent_successfully");
        this.form.content = "";
      } catch (error) {
        this.errorMessage =
            error?.response?.data?.message || this.$t("errors.500");
      }
    },
    async sendMessage() {
      const formData = new FormData();
      const groupFormData = new FormData();
      const userId = JSON.parse(this.secureLocalStorage("user")).id;

      formData.append("message", this.form.content);
      formData.append("sender_user_id", userId);
      groupFormData.append("users[]", [this.selectedUser]);

      try {
        const groupResponse = await axios.post(
            "api/store-group",
            groupFormData,
            {
              headers: this.headers,
            }
        );
        const groupId = groupResponse?.data?.id ?? groupResponse?.data;
        formData.append("message_group_id", groupId);

        const response = await axios.post("api/send-message", formData, {
          headers: this.headers,
        });
        UIkit.modal("#message_modal_user").hide();

        if (response?.data?.message) {
          this.errorMessage = response.data.message;
          return;
        }

        this.successMessageSend = this.$t("general.message_sent_successfully");
        this.form.content = "";
      } catch (error) {
        UIkit.modal("#message_modal_user").hide();
        this.errorMessage =
            error?.response?.data?.message || this.$t("errors.500");
      }
    },
    getScormEducationUrl(contentId) {
      const url =
          this.scormUrl +
          this.$route.params.id +
          "/training-content/" +
          contentId;

      this.$store
          .dispatch(GET_ITEM_DETAIL_BY_ID, {
            url,
            acceptPromise: false,
          })
          .then((result) => {
            if (result.status) {
              this.scormPackageItemUrl = result.data;
            }
          });
    },
    resetDetailState() {
      this.activeItem = null;
      this.selectedUser = null;
      this.item = {};
      this.form.content = "";
      this.selected_content = null;
      this.scormPackageItemUrl = null;
      this.anzeraPackageItemUrl = null;
      this.successMessage = null;
      this.successMessageSend = null;
      this.errorMessage = null;
      this.request_number = 0;
    },
    loadTrainingFromRoute(trainingId) {
      if (!trainingId) {
        this.$router.go(-1);
        return;
      }

      this.isLoading = true;
      this.resetDetailState();
      const requestId = ++this.detailRequestId;
      this.getTrainingDetails(trainingId, requestId);
    },
    async getTrainingDetails(trainingId, requestId) {
      try {
        const result = await this.$store.dispatch(GET_ITEM_DETAIL_BY_ID, {
          url: this.restUrl + trainingId,
          filters: { with_requests: true },
          acceptPromise: false,
        });

        if (requestId === this.detailRequestId && result.status) {
          this.item = result.data;
          this.request_number = Number(result.data.users_pending_requests) || 0;
        }
      } catch (error) {
        if (requestId === this.detailRequestId) {
          this.errorMessage =
              error?.response?.data?.message || this.$t("errors.500");
        }
      } finally {
        if (requestId === this.detailRequestId) {
          this.isLoading = false;
        }
      }
    },
    purchase() {
      this.request_number = 0;

      const formData = new FormData();
      formData.append("request_type_id", 1);

      this.$store
          .dispatch(CREATE_ITEM, {
            url: this.trainingPurchaseUrl + this.$route.params.id,
            contents: formData,
            acceptPromise: true,
          })
          .then((result) => {
            if (this.error) {
              this.errorMessage = this.error;
              return;
            }

            if (!Array.isArray(this.item.purchases)) {
              this.$set(this.item, "purchases", []);
            }
            this.item.purchases.unshift(result.data);
            this.switcherChange(1);
            this.getUserPurchaseRequestCount();
          });
    },
    getUserPurchaseRequestCount() {
      this.$store.dispatch(
          PURCHASE_MODULE_NAME + "/" + GET_PURCHASE_USER_REQUEST_COUNT,
          { url: "api/purchase-request-count" }
      );
    },
    removePurchase(id, index) {
      this.$store
          .dispatch(DELETE_ITEM_BY_ID, {
            url: this.purchaseUrl + id,
            acceptPromise: true,
          })
          .then(() => {
            if (this.error) {
              this.errorMessage = this.error;
              return;
            }

            this.item.purchases.splice(index, 1);
            this.getUserPurchaseRequestCount();
          });
    },
    resetMessages() {
      this.errorMessage = null;
      this.successMessage = null;
      this.successMessageSend = null;
    },
    switcherChange(index) {
      const switcherElement = document.getElementById(
          "education_detail_switcher_tab"
      );
      if (switcherElement) {
        UIkit.switcher(switcherElement).show(index);
      }
    },
  },
};
</script>

<style>
.education-details-page {
  --education-primary: var(--primaryColor, #3e416d);
  --education-text: #1e293b;
  --education-muted: #64748b;
  --education-border: #e2e8f0;
  min-height: calc(100vh - var(--app-header-height-desktop, 70px));
  margin: 0;
  padding: 32px 0;
  background: #f6f7fb;
}

.education-details-container {
  padding-right: 24px;
  padding-left: 24px;
}

.education-details-layout {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(0, 2fr);
  grid-template-rows: auto 1fr;
  align-items: start;
  gap: 24px 32px;
}

.education-details-loading {
  overflow: hidden;
  grid-column: 1 / -1;
  min-height: 60vh;
  border: 1px solid var(--education-border);
  border-radius: 14px;
  background: #ffffff;
}

.education-details-layout__hero {
  grid-column: 2;
  grid-row: 1;
}

.education-details-layout__sidebar {
  grid-column: 1;
  grid-row: 1 / span 2;
}

.education-details-layout__tabs {
  grid-column: 2;
  grid-row: 2;
}

@media (max-width: 959px) {
  .education-details-layout {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto;
  }

  .education-details-layout__hero {
    grid-column: 1;
    grid-row: 1;
  }

  .education-details-layout__sidebar {
    grid-column: 1;
    grid-row: 2;
  }

  .education-details-layout__tabs {
    grid-column: 1;
    grid-row: 3;
  }

  /* Padding azaltmasi eskiden 767'deydi; layout ise burada tek kolona
     dusuyor. 768-959 araliginda tek kolon + desktop padding kaliyordu. */
  .education-details-page {
    min-height: calc(100vh - var(--app-header-height, 60px));
    padding: 16px 0 24px;
  }

  .education-details-container {
    padding-right: 16px;
    padding-left: 16px;
  }

  .education-details-layout {
    gap: 16px;
  }

  /* Sayfa padding'i ile sekme paneli padding'i ust uste biniyordu
     (16 + 24 = 40px). Panel sayfayla ayni olcuye cekiliyor. */
  .education-details-layout__tabs .company-tabs__content > li {
    padding: 16px;
  }
}

@media (max-width: 767px) {
  .education-details-page {
    padding: 12px 0 16px;
  }

  .education-details-container {
    padding-right: 12px;
    padding-left: 12px;
  }

  .education-details-layout {
    gap: 12px;
  }

  .education-details-layout__tabs .company-tabs__content > li {
    padding: 12px;
  }
}
</style>
